'use client';

import { useEffect, useRef, useState } from 'react';
import { consumeToolUsage } from '@/lib/browser-tool-usage';

export type TimeToolVariant = 'online-stopwatch' | 'countdown-timer' | 'pomodoro-timer';

// Countdowns round up so a fresh 15-minute timer reads 15:00, not 14:59, and hits 00:00
// exactly when it ends; the stopwatch rounds down.
function formatMs(ms: number, roundUp = false): string {
  const total = Math.max(0, roundUp ? Math.ceil(ms / 1000) : Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

const POMODORO_WORK = 25 * 60 * 1000;
const POMODORO_BREAK = 5 * 60 * 1000;

// A short two-note chime, synthesized so the page ships no audio asset.
function playChime() {
  if (typeof window === 'undefined') return;
  const AudioCtor =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AudioCtor) return;
  try {
    const ctx = new AudioCtor();
    const now = ctx.currentTime;
    [880, 1320].forEach((frequency, index) => {
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();
      oscillator.frequency.value = frequency;
      oscillator.type = 'sine';
      const start = now + index * 0.22;
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.25, start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.2);
      oscillator.connect(gain).connect(ctx.destination);
      oscillator.start(start);
      oscillator.stop(start + 0.22);
    });
    window.setTimeout(() => void ctx.close(), 900);
  } catch {
    // An autoplay-blocked chime is not worth breaking the timer over.
  }
}

export default function BrowserTimeTool({
  variant,
  initialMinutes,
  initialSeconds,
  toolSlug,
}: {
  variant: TimeToolVariant;
  // Preset pages (/tools/15-minute-timer, /tools/30-second-timer) load the duration
  // ready to start, so the page shows 15:00 on arrival instead of an empty timer.
  initialMinutes?: number;
  initialSeconds?: number;
  toolSlug?: string;
}) {
  const presetMinutes = initialMinutes ?? (initialSeconds ? 0 : 5);
  const presetSeconds = initialSeconds ?? 0;
  const [running, setRunning] = useState(false);
  // Separates "loaded but never started" (button says Start) from "paused" (Resume).
  const [started, setStarted] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [remaining, setRemaining] = useState(
    variant === 'pomodoro-timer'
      ? POMODORO_WORK
      : initialMinutes || initialSeconds
        ? (presetMinutes * 60 + presetSeconds) * 1000
        : 0,
  );
  const [inputMinutes, setInputMinutes] = useState(presetMinutes);
  const [inputSeconds, setInputSeconds] = useState(presetSeconds);
  const reported = useRef(false);
  const report = () => {
    if (reported.current) return;
    reported.current = true;
    void consumeToolUsage(toolSlug ?? variant, '').catch(() => undefined);
  };
  const durationMs = () =>
    Math.max(1, Math.round(Math.max(0, inputMinutes) * 60 + Math.max(0, inputSeconds))) * 1000;
  const [pomodoroMode, setPomodoroMode] = useState<'work' | 'break'>('work');
  const lastTick = useRef<number>(0);

  useEffect(() => {
    if (!running) return;
    lastTick.current = performance.now();
    const id = window.setInterval(() => {
      const now = performance.now();
      const delta = now - lastTick.current;
      lastTick.current = now;
      if (variant === 'online-stopwatch') {
        setElapsed((prev) => prev + delta);
      } else {
        setRemaining((prev) => {
          const next = prev - delta;
          if (next <= 0) {
            // A timer that ends in silence is useless in a background tab, which is
            // where people leave it.
            playChime();
            if (variant === 'pomodoro-timer') {
              const nextMode = pomodoroMode === 'work' ? 'break' : 'work';
              setPomodoroMode(nextMode);
              return nextMode === 'work' ? POMODORO_WORK : POMODORO_BREAK;
            }
            setRunning(false);
            setStarted(false);
            return 0;
          }
          return next;
        });
      }
    }, 100);
    return () => window.clearInterval(id);
  }, [running, variant, pomodoroMode]);

  function startCountdown() {
    // Pressing Start on a running timer used to restart it from the top. Resume instead,
    // and only load a fresh duration when the timer has not started or is at zero.
    report();
    if (running) {
      setRunning(false);
      return;
    }
    if (!started || remaining <= 0) {
      setRemaining(durationMs());
    }
    setStarted(true);
    setRunning(true);
  }

  function reset() {
    setRunning(false);
    setStarted(false);
    setElapsed(0);
    if (variant === 'pomodoro-timer') {
      setPomodoroMode('work');
      setRemaining(POMODORO_WORK);
    } else {
      // Back to the loaded duration, so a preset page is one click from a rerun.
      setRemaining(durationMs());
    }
  }

  function setDuration(minutes: number, seconds: number) {
    setInputMinutes(minutes);
    setInputSeconds(seconds);
    if (!running) {
      setStarted(false);
      setRemaining(
        Math.max(0, Math.round(Math.max(0, minutes) * 60 + Math.max(0, seconds))) * 1000,
      );
    }
  }

  const display = variant === 'online-stopwatch' ? formatMs(elapsed) : formatMs(remaining, true);
  const badge =
    variant === 'online-stopwatch'
      ? 'Stopwatch'
      : variant === 'countdown-timer'
        ? 'Timer'
        : 'Pomodoro';
  const title =
    variant === 'online-stopwatch'
      ? 'Online stopwatch'
      : variant === 'countdown-timer'
        ? 'Online countdown timer'
        : 'Pomodoro timer';

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">{badge}</span>
        <h2 className="tool-live-title">{title}</h2>
        <p className="tool-live-subtitle">
          Runs entirely in your browser. Nothing is uploaded and no account is needed.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          {variant === 'countdown-timer' ? (
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <label className="tool-live-field">
                <span>Minutes</span>
                <input
                  inputMode="numeric"
                  min={0}
                  onChange={(event) => setDuration(Number(event.target.value) || 0, inputSeconds)}
                  type="number"
                  value={inputMinutes}
                />
              </label>
              <label className="tool-live-field">
                <span>Seconds</span>
                <input
                  inputMode="numeric"
                  max={59}
                  min={0}
                  onChange={(event) =>
                    setDuration(inputMinutes, Math.min(59, Number(event.target.value) || 0))
                  }
                  type="number"
                  value={inputSeconds}
                />
              </label>
            </div>
          ) : null}
          {variant === 'pomodoro-timer' ? (
            <p className="tool-live-panel-note">
              Current phase:{' '}
              <strong>{pomodoroMode === 'work' ? 'Focus (25 min)' : 'Break (5 min)'}</strong>
            </p>
          ) : null}
          <div className="tool-live-actions">
            {variant === 'countdown-timer' ? (
              <button className="button button-secondary" onClick={startCountdown} type="button">
                {running ? 'Pause' : started && remaining > 0 ? 'Resume' : 'Start'}
              </button>
            ) : (
              <button
                className="button button-secondary"
                onClick={() => {
                  report();
                  setRunning((r) => !r);
                }}
                type="button"
              >
                {running ? 'Pause' : 'Start'}
              </button>
            )}
            <button className="button button-secondary" onClick={reset} type="button">
              Reset
            </button>
          </div>
        </section>

        <section className="tool-live-preview">
          <div className="tool-live-stats">
            <p
              className="tool-live-title"
              style={{ fontSize: 48, fontVariantNumeric: 'tabular-nums' }}
            >
              {display}
            </p>
          </div>
          <p className="tool-live-panel-note">Timing runs locally in your browser tab.</p>
        </section>
      </div>
    </div>
  );
}
